import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UsersService } from '@common-services/api/users.service';
import { DialogService } from '@common-services/ui/dialog.service';
import { LoaderService } from '@common-services/ui/loader.service';
import { AuthService } from '@common-services/util/auth.service';
import { MESSAGETYPE } from '@constants/dialog.const';
import { AppModuleInjector } from '@dice/app.module';
import { MessageDialogPayload } from '@dice/app/types/dialog.types';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/** Http interceptor for handling request errors */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private cookieService = inject(CookieService);
  private users = inject(UsersService);
  private loaderService = inject(LoaderService);
  private dialogService = inject(DialogService);

  intercept(httpRequest: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const cleanHeadersForRequest = httpRequest.headers.delete('errorDialogBehavior').delete('handleNoContentAsError');
    const requestSansDialogBehaviorHeader = httpRequest.clone({ headers: cleanHeadersForRequest });
    return next.handle(requestSansDialogBehaviorHeader).pipe(
      switchMap((event: HttpEvent<unknown>) => {
        if (httpRequest.headers.get('handleNoContentAsError') === 'skip') {
          return of(event);
        }
        return this.handleIncorrectNoContentResponses(event, httpRequest);
      }),
      catchError((err: HttpErrorResponse) => {
        return this.handleError(httpRequest, err);
      }),
    );
  }

  /**
   * For some reason some backends send 204 No Content instead of 404 Not Found for non-found requested (GET) resources.
   * @param event
   * @param req
   */
  private handleIncorrectNoContentResponses(
    event: HttpEvent<unknown>,
    req: HttpRequest<unknown>,
  ): Observable<HttpEvent<unknown>> {
    if (!(event instanceof HttpResponse) || event.status !== 204 || req.method !== 'GET') {
      return of(event);
    }
    return throwError(
      new HttpErrorResponse({
        error: event.statusText,
        ...event,
      }),
    );
  }

  private handleError = (httpRequest: HttpRequest<unknown>, err: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
    if (err.error instanceof ErrorEvent) {
      // A client-side or network error occurred
      customLogger.error('[ErrorInterceptor] - Client-side or network error occurred:', err.error.message);
    } else {
      // SERVER returned an unsuccessful response code
      customLogger.warn(`[ErrorInterceptor] - Backend returned code ${err.status}, body was: ${err.error}`);

      /**
       * 204 Handle Markus incorrectly sending 204 instead of 404, when a requested resource was not found -> show popup
       */
      if (err.status === 204) {
        this.showError(httpRequest, err);
      }

      /**
       * 400 BAD_REQUEST (User Input Error) -> show popup
       */
      if (err.status === 400 && err.error && err.error.message === 'Token has expired') {
        this.doLogout();
        return;
      }

      if (err.status === 400) {
        this.showError(httpRequest, err);
      }

      /**
       * 401 UNAUTHORIZED -> show popup
       */
      if (err.status === 401) {
        this.doLogout();
      }

      /**
       * 403 FORBIDDEN ERROR -> logout if no token available
       */
      if (err.status === 403) {
        this.doLogout();
      }

      /**
       * 404 NOT FOUND ERROR -> show popup
       */
      if (err.status === 404) {
        this.showError(httpRequest, err);
      }

      /**
       * 405 METHOD NOT ALLOWED SERVER ERROR -> show popup
       */
      if (err.status === 405) {
        this.showError(httpRequest, err);
      }

      /**
       * 415 UNSUPPORTED MEDIA TYPE ERROR -> show popup
       */
      if (err.status === 415) {
        this.showError(httpRequest, err);
      }

      /**
       * 500 INTERNAL SERVER ERROR -> show popup
       */
      if (err.status === 500) {
        this.showError(httpRequest, err);
      }
    }
    // return an observable with a user-facing error message
    // eslint-disable-next-line consistent-return
    return throwError(err);
  };

  private showError(httpRequest: HttpRequest<unknown>, httpErrorResponse: HttpErrorResponse): void {
    const errorDialogBehavior = httpRequest.headers.get('errorDialogBehavior');
    if (errorDialogBehavior === 'skip') {
      customLogger.error(`[ErrorInterceptor] - Error: ${httpErrorResponse.message} -> skipping POPUP!`);
    } else {
      customLogger.error(`[ErrorInterceptor] - Error: ${httpErrorResponse.message} -> show POPUP!`);
      const message = (httpErrorResponse.error && httpErrorResponse.error.message) || httpErrorResponse.statusText;
      const data: MessageDialogPayload = {
        type: MESSAGETYPE.ERROR,
        title: `${httpErrorResponse.status} - ${message}`,
        details: null,
      };
      if ('data.message' in httpErrorResponse) {
        // @ts-ignore
        data.details = httpErrorResponse.data.message;
      } else if (httpErrorResponse.error && httpErrorResponse.statusText === httpErrorResponse.error) {
        data.details = null;
      } else {
        data.details = httpErrorResponse.error;
      }
      this.dialogService.showMessageDialog(() => {
        if (errorDialogBehavior === 'redirect') {
          window.history.go(-1);
        }
        this.loaderService.hideLoader();
      }, data);
    }
  }

  private doLogout(): void {
    customLogger.error('[ErrorInterceptor] - LOGOUT!');
    const authService = AppModuleInjector.get(AuthService);
    authService.logout();
  }
}
